import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Generic section editor used by AdminSbtSectionsHost.
 *
 * Loads /api/sbt-site/sections/{sectionKey}, gives the admin a
 * schema-shaped form (or JSON blob editor for complex sections), and
 * writes back via PUT /api/admin/sbt-site/sections/{sectionKey}.
 *
 * The Astro marketing site rebuilds at deploy-time and picks up the
 * new content via its build-time fetch (with an inline hardcoded
 * fallback so nothing is ever blank).
 */
function useSection(key) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sbt-site/sections/${key}`);
      setData(r.data.data || {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Failed to load ${key}`); }
    finally { setLoading(false); }
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/admin/sbt-site/sections/${key}`, { data: payload });
      toast.success("Saved. Auto-publishing to live site in ~5 seconds…");
      setData(payload);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  useEffect(() => { load(); }, [key]);
  return { data, setData, loading, saving, save };
}

/* ────────── Section 1 — APPROACH ────────── */
export function AdminSbtApproachSection() {
  const { data, loading, saving, save } = useSection("approach");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (loading || !form) return <Loading label="Loading Approach…"/>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, j) => j === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { n: String((f.items || []).length + 1).padStart(2, "0"), title: "New Step", body: "" }] }));
  const rmItem  = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const move = (i, dir) => setForm((f) => {
    const arr = [...f.items]; const t = i + dir;
    if (t < 0 || t >= arr.length) return f;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    return { ...f, items: arr };
  });

  return (
    <SectionShell title="Approach — 4 discipline gates" onSave={() => save(form)} saving={saving}>
      <TextField label="Eyebrow" value={form.eyebrow} onChange={(v) => setF("eyebrow", v)} testId="approach-eyebrow"/>
      <TextField label="Title (HTML allowed)" value={form.title} onChange={(v) => setF("title", v)} testId="approach-title"/>
      <TextAreaField label="Subtitle" value={form.subtitle} onChange={(v) => setF("subtitle", v)} testId="approach-subtitle"/>
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-semibold">Items</Label>
          <Button size="sm" variant="outline" onClick={addItem} data-testid="approach-add-item"><Plus className="w-3.5 h-3.5 mr-1"/>Add Item</Button>
        </div>
        {(form.items || []).map((it, i) => (
          <div key={i} className="border rounded p-3 mb-2" data-testid={`approach-item-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-[60px_120px_1fr_60px] gap-2 items-start">
              <div>
                <Label className="text-[11px]">N°</Label>
                <Input value={it.n || ""} onChange={(e) => setItem(i, "n", e.target.value)}/>
              </div>
              <div>
                <Label className="text-[11px]">Title</Label>
                <Input value={it.title || ""} onChange={(e) => setItem(i, "title", e.target.value)}/>
              </div>
              <div>
                <Label className="text-[11px]">Body</Label>
                <Textarea rows={2} value={it.body || ""} onChange={(e) => setItem(i, "body", e.target.value)}/>
              </div>
              <div className="flex flex-col gap-1 pt-4">
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => move(i, -1)}><ChevronUp className="w-4 h-4"/></button>
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => move(i, 1)}><ChevronDown className="w-4 h-4"/></button>
                <button type="button" className="text-red-500 hover:text-red-700" onClick={() => rmItem(i)}><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ────────── Section 2 — CTA MEGA ────────── */
export function AdminSbtCtaMegaSection() {
  const { data, loading, saving, save } = useSection("cta_mega");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (loading || !form) return <Loading label="Loading CTA Banner…"/>;
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <SectionShell title="CTA Mega — Big Cyan Banner" onSave={() => save(form)} saving={saving}>
      <TextField label="Headline" value={form.headline} onChange={(v) => setF("headline", v)} testId="cta-headline"/>
      <TextAreaField label="Body / Description" value={form.body} onChange={(v) => setF("body", v)} testId="cta-body"/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
        <TextField label="CTA Button Label" value={form.cta_label} onChange={(v) => setF("cta_label", v)} testId="cta-label"/>
        <TextField label="CTA Link" value={form.cta_href} onChange={(v) => setF("cta_href", v)} testId="cta-href"/>
      </div>
    </SectionShell>
  );
}

/* ────────── Section 3 — FOOTER ────────── */
export function AdminSbtFooterSection() {
  const { data, loading, saving, save } = useSection("footer");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (loading || !form) return <Loading label="Loading Footer…"/>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...(f.socials || {}), [k]: v } }));

  const addCol = () => setForm((f) => ({ ...f, columns: [...(f.columns || []), { heading: "New Column", items: [] }] }));
  const rmCol = (ci) => { if (!window.confirm("Remove this entire column?")) return; setForm((f) => ({ ...f, columns: f.columns.filter((_, i) => i !== ci) })); };
  const setCol = (ci, k, v) => setForm((f) => ({ ...f, columns: f.columns.map((c, i) => i === ci ? { ...c, [k]: v } : c) }));
  const addLink = (ci) => setForm((f) => ({ ...f, columns: f.columns.map((c, i) => i === ci ? { ...c, items: [...(c.items || []), { label: "", href: "" }] } : c) }));
  const setLink = (ci, li, k, v) => setForm((f) => ({ ...f, columns: f.columns.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => j === li ? { ...it, [k]: v } : it) } : c) }));
  const rmLink = (ci, li) => setForm((f) => ({ ...f, columns: f.columns.map((c, i) => i === ci ? { ...c, items: c.items.filter((_, j) => j !== li) } : c) }));

  return (
    <SectionShell title="Footer — About + link columns + copyright" onSave={() => save(form)} saving={saving}>
      <TextAreaField label="About paragraph" value={form.about} onChange={(v) => setF("about", v)} testId="footer-about"/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextField label="Email" value={form.email} onChange={(v) => setF("email", v)} testId="footer-email"/>
        <TextField label="Phone" value={form.phone} onChange={(v) => setF("phone", v)} testId="footer-phone"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TextField label="LinkedIn URL" value={form.socials?.linkedin || ""} onChange={(v) => setSocial("linkedin", v)}/>
        <TextField label="Twitter/X URL" value={form.socials?.twitter || ""} onChange={(v) => setSocial("twitter", v)}/>
        <TextField label="Facebook URL" value={form.socials?.facebook || ""} onChange={(v) => setSocial("facebook", v)}/>
      </div>
      <TextField label="Copyright" value={form.copyright} onChange={(v) => setF("copyright", v)} testId="footer-copyright"/>

      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-semibold">Link Columns</Label>
          <Button size="sm" variant="outline" onClick={addCol} data-testid="footer-add-col"><Plus className="w-3.5 h-3.5 mr-1"/>Add Column</Button>
        </div>
        {(form.columns || []).map((col, ci) => (
          <div key={ci} className="border rounded-lg p-4 mb-3 bg-white" data-testid={`footer-col-${ci}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1">
                <Label className="text-[11px]">Column Heading</Label>
                <Input value={col.heading || ""} onChange={(e) => setCol(ci, "heading", e.target.value)}/>
              </div>
              <Button size="sm" variant="ghost" onClick={() => rmCol(ci)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
            </div>
            <div className="space-y-2">
              {(col.items || []).map((it, li) => (
                <div key={li} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <Input placeholder="Label" value={it.label || ""} onChange={(e) => setLink(ci, li, "label", e.target.value)}/>
                  <Input placeholder="/path/or/https://…" value={it.href || ""} onChange={(e) => setLink(ci, li, "href", e.target.value)}/>
                  <Button size="sm" variant="ghost" onClick={() => rmLink(ci, li)}><Trash2 className="w-3.5 h-3.5 text-red-500"/></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addLink(ci)}><Plus className="w-3 h-3 mr-1"/>Add link</Button>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ────────── Section 4 — PARTNER ECOSYSTEM (chips + counts) ────────── */
export function AdminSbtPartnerEcoSection() {
  const { data, loading, saving, save } = useSection("partner_ecosystem");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (loading || !form) return <Loading label="Loading Partner Ecosystem…"/>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChip = (i, k, v) => setForm((f) => ({ ...f, chips: f.chips.map((c, j) => j === i ? { ...c, [k]: v } : c) }));

  return (
    <SectionShell title="Partner Ecosystem — orbital chips (layout preserved)" onSave={() => save(form)} saving={saving}>
      <TextField label="Eyebrow" value={form.eyebrow} onChange={(v) => setF("eyebrow", v)}/>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TextField label="Title Pre" value={form.title_pre} onChange={(v) => setF("title_pre", v)}/>
        <TextField label="Title Accent (cyan)" value={form.title_accent} onChange={(v) => setF("title_accent", v)}/>
        <TextField label="Title Post" value={form.title_post} onChange={(v) => setF("title_post", v)}/>
      </div>
      <TextAreaField label="Lead paragraph (HTML allowed — supports <strong>)" value={form.lead} onChange={(v) => setF("lead", v)}/>

      <div className="mt-4">
        <Label className="text-sm font-semibold">Orbital Chips (5 fixed positions)</Label>
        <p className="text-xs text-slate-500 mb-2">Only labels, colors and counts are editable — orbit layout is preserved by design.</p>
        {(form.chips || []).map((c, i) => (
          <div key={i} className="border rounded-lg p-3 mb-2 bg-white" data-testid={`chip-${c.key}`}>
            <div className="flex items-center gap-2 mb-2 text-xs">
              <span className="w-4 h-4 rounded" style={{ background: c.color || c.c || "#ccc" }}/>
              <span className="font-mono uppercase text-[10px] tracking-wider text-slate-500">{c.key}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div><Label className="text-[11px]">Label</Label><Input value={c.label || ""} onChange={(e) => setChip(i, "label", e.target.value)}/></div>
              <div><Label className="text-[11px]">Pill text</Label><Input value={c.pill || ""} onChange={(e) => setChip(i, "pill", e.target.value)}/></div>
              <div><Label className="text-[11px]">Count</Label><Input type="number" value={c.count ?? 0} onChange={(e) => setChip(i, "count", parseInt(e.target.value) || 0)}/></div>
              <div>
                <Label className="text-[11px]">Color hex</Label>
                <div className="flex gap-1">
                  <input type="color" value={c.color || c.c || "#00afd1"} onChange={(e) => setChip(i, "color", e.target.value)} className="h-9 w-11 rounded border"/>
                  <Input value={c.color || c.c || ""} onChange={(e) => setChip(i, "color", e.target.value)} className="text-xs"/>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ────────── Section 5 — AI SEARCH NETWORK (marquee logos) ────────── */
export function AdminSbtAiSearchSection() {
  const { data, loading, saving, save } = useSection("ai_search_network");
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ ...data, logos: data.logos || [] }); }, [data]);
  if (loading || !form) return <Loading label="Loading AI Search Network…"/>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLogo = (i, k, v) => setForm((f) => ({ ...f, logos: f.logos.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const addLogo = () => setForm((f) => ({ ...f, logos: [...f.logos, { name: "New Brand", slug: "", color: "", fg: "#ffffff", bg: "#0f172a" }] }));
  const rmLogo = (i) => setForm((f) => ({ ...f, logos: f.logos.filter((_, j) => j !== i) }));

  return (
    <SectionShell title="AI Search Network — marquee logo lane" onSave={() => save(form)} saving={saving}>
      <TextField label="Eyebrow" value={form.eyebrow} onChange={(v) => setF("eyebrow", v)}/>
      <TextField label="Title (HTML allowed)" value={form.title} onChange={(v) => setF("title", v)}/>
      <TextAreaField label="Subtitle" value={form.subtitle} onChange={(v) => setF("subtitle", v)}/>

      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-semibold">Logo tiles</Label>
          <Button size="sm" variant="outline" onClick={addLogo}><Plus className="w-3.5 h-3.5 mr-1"/>Add Logo</Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          For SVG icons: set <code className="bg-slate-100 px-1 rounded">slug</code> to a
          <a href="https://simpleicons.org/" target="_blank" rel="noreferrer" className="text-cyan-600 underline mx-1">simpleicons.org</a>
          slug (e.g. <code className="bg-slate-100 px-1 rounded">openai</code>) and <code className="bg-slate-100 px-1 rounded">color</code> to a hex (no #).
          Otherwise leave slug empty and use <code className="bg-slate-100 px-1 rounded">fg</code>/<code className="bg-slate-100 px-1 rounded">bg</code> for a text tile.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {form.logos.map((l, i) => (
            <div key={i} className="border rounded p-3 bg-white" data-testid={`ai-logo-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Name</Label><Input value={l.name || ""} onChange={(e) => setLogo(i, "name", e.target.value)}/></div>
                <div><Label className="text-[11px]">SimpleIcons slug (opt)</Label><Input value={l.slug || ""} onChange={(e) => setLogo(i, "slug", e.target.value)}/></div>
                <div><Label className="text-[11px]">Icon color (hex, no #)</Label><Input value={l.color || ""} onChange={(e) => setLogo(i, "color", e.target.value)}/></div>
                <div><Label className="text-[11px]">Text bg</Label><input type="color" value={l.bg || "#0f172a"} onChange={(e) => setLogo(i, "bg", e.target.value)} className="h-9 w-full rounded border"/></div>
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="ghost" onClick={() => rmLogo(i)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

/* ────────── shared UI bits ────────── */
/* ────────── Legal Pages — Privacy Policy & Terms of Service ────────── */
function LegalPageEditor({ sectionKey, heading, pagePath, termlyNote }) {
  const { data, loading, saving, save } = useSection(sectionKey);
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (loading || !form) return <Loading label={`Loading ${heading}…`}/>;
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <SectionShell title={`${heading} — ${pagePath}`} onSave={() => save(form)} saving={saving}>
      <TextField label="Page title" value={form.title} onChange={(v) => setF("title", v)} testId={`${sectionKey}-title`}/>
      <TextAreaField label="Intro (shown under the title)" value={form.intro} onChange={(v) => setF("intro", v)} testId={`${sectionKey}-intro`}/>
      <div className="mt-3">
        <Label className="text-sm font-semibold">Page content (HTML)</Label>
        {termlyNote && (
          <p className="text-[11px] text-slate-500 mt-1 mb-1">
            Leave EMPTY to keep showing the Termly-managed policy embed. Any content typed here replaces the Termly embed entirely.
          </p>
        )}
        <Textarea
          rows={14}
          className="font-mono text-xs mt-1"
          data-testid={`${sectionKey}-body-html`}
          value={form.body_html || ""}
          onChange={(e) => setF("body_html", e.target.value)}
          placeholder="<h2>Section heading</h2>\n<p>Paragraph…</p>"
        />
      </div>
    </SectionShell>
  );
}

export function AdminSbtLegalPagesSection() {
  return (
    <div className="space-y-10" data-testid="legal-pages-editor">
      <LegalPageEditor sectionKey="legal_privacy" heading="Privacy Policy" pagePath="/privacy-policy/" termlyNote />
      <LegalPageEditor sectionKey="legal_terms" heading="Terms of Service" pagePath="/terms/" />
    </div>
  );
}

function Loading({ label }) {
  return <div className="p-4 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>{label}</div>;
}
function SectionShell({ title, onSave, saving, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <Button size="sm" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
          Save Changes
        </Button>
      </div>
      <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 mb-3">
        ⚡ Auto-publish is on — any Save automatically rebuilds the marketing site (~5s). Watch the "Publish Status" pill in the header for live progress.
      </p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function TextField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>
    </div>
  );
}
function TextAreaField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>
    </div>
  );
}
